/**
 * Module Info Settings Component
 * Controls visibility of module info panels and layout info
 *
 * URL Params:
 * - ?moduleInfo=false hoặc ?moduleInfo=0 → ẩn module info
 * - ?basicInfo=false hoặc ?basicInfo=0 → ẩn basic info
 */
export default class ModuleInfoSettings {
  constructor() {
    this.STORAGE_KEY = 'moduleInfoSettings';
    this.panel = document.querySelector('.module-info-settings__panel');
    this.toggleBtn = document.querySelector('.module-info-settings__toggle');
    this.moduleInfoCheckbox = document.getElementById('toggle-module-info');
    this.layoutInfoCheckbox = document.getElementById('toggle-layout-info');

    // Kiểm tra elements tồn tại
    if (!this.panel || !this.toggleBtn || !this.moduleInfoCheckbox) {
      console.warn('[ModuleInfoSettings] Required elements not found');
      return;
    }
  }

  init() {
    // Guard: Skip init if required elements not found
    if (!this.panel || !this.toggleBtn || !this.moduleInfoCheckbox) {
      return;
    }

    // Load settings (localStorage + URL params)
    const settings = this.loadSettings();
    this.applySettings(settings);

    // Bind events
    this.toggleBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.togglePanel();
    });

    this.moduleInfoCheckbox.addEventListener('change', () => {
      this.saveSettings();
    });

    if (this.layoutInfoCheckbox) {
      this.layoutInfoCheckbox.addEventListener('change', () => {
        this.saveSettings();
      });
    }
  }

  /**
   * Đọc URL params
   * @returns {Object} Settings từ URL params
   */
  getUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const urlSettings = {};

    // Parse moduleInfo param
    if (params.has('moduleInfo')) {
      const value = params.get('moduleInfo');
      urlSettings.showModuleInfo = value === 'true' || value === '1';
    }

    // Parse basicInfo param (layout info)
    if (params.has('basicInfo')) {
      const value = params.get('basicInfo');
      urlSettings.showLayoutInfo = value === 'true' || value === '1';
    }

    return urlSettings;
  }

  /**
   * Load settings từ localStorage và URL params
   * URL params có priority cao hơn localStorage
   */
  loadSettings() {
    try {
      // Default settings
      const defaultSettings = {
        showModuleInfo: true,
        showLayoutInfo: true
      };

      // Load từ localStorage
      const saved = localStorage.getItem(this.STORAGE_KEY);
      const savedSettings = saved ? JSON.parse(saved) : {};

      // Load từ URL params (override localStorage)
      const urlSettings = this.getUrlParams();

      // Merge: defaults < localStorage < URL params
      const finalSettings = {
        ...defaultSettings,
        ...savedSettings,
        ...urlSettings
      };

      return finalSettings;
    } catch (error) {
      console.error('[ModuleInfoSettings] Error loading settings:', error);
      return {
        showModuleInfo: true,
        showLayoutInfo: true
      };
    }
  }

  /**
   * Apply settings to UI
   */
  applySettings(settings) {
    // Update body classes
    document.body.classList.toggle('hide-module-info', !settings.showModuleInfo);
    document.body.classList.toggle('hide-layout-info', !settings.showLayoutInfo);

    // Update checkboxes
    this.moduleInfoCheckbox.checked = settings.showModuleInfo;
    if (this.layoutInfoCheckbox) {
      this.layoutInfoCheckbox.checked = settings.showLayoutInfo;
    }
  }

  /**
   * Save settings to localStorage và update URL params
   * @param {boolean} updateUrl - Có update URL params không (default: true)
   */
  saveSettings(updateUrl = true) {
    const settings = {
      showModuleInfo: this.moduleInfoCheckbox.checked,
      showLayoutInfo: this.layoutInfoCheckbox ? this.layoutInfoCheckbox.checked : true
    };

    try {
      // Save to localStorage
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(settings));

      // Apply settings
      this.applySettings(settings);

      // Update URL params
      if (updateUrl) {
        this.updateUrlParams(settings);
      }
    } catch (error) {
      console.error('[ModuleInfoSettings] Error saving settings:', error);
    }
  }

  /**
   * Update URL params với settings hiện tại
   * @param {Object} settings - Settings object
   */
  updateUrlParams(settings) {
    try {
      const url = new URL(window.location);

      // Update moduleInfo param
      if (settings.showModuleInfo === false) {
        url.searchParams.set('moduleInfo', '0');
      } else {
        url.searchParams.delete('moduleInfo');
      }

      // Update basicInfo param
      if (settings.showLayoutInfo === false) {
        url.searchParams.set('basicInfo', '0');
      } else {
        url.searchParams.delete('basicInfo');
      }

      // Update URL without reload
      window.history.replaceState({}, '', url);
    } catch (error) {
      console.error('[ModuleInfoSettings] Error updating URL params:', error);
    }
  }

  /**
   * Toggle panel open/close
   */
  togglePanel() {
    this.panel.classList.toggle('is-open');
  }
}

// Auto-initialize
new ModuleInfoSettings().init();
